import React, { useEffect, useState } from "react";
import API from "../../api/api";
import Swal from "sweetalert2";
import { FaPlus, FaMedal, FaCalendarAlt, FaFileAlt, FaCheckCircle, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";
import { resolveAssetUrl } from "../../api/config";

const MyAchievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    event_name: "",
    event_location: "",
    event_level: "",
    medal: "None",
    event_date: "",
    notes: "",
  });
  const [documentFile, setDocumentFile] = useState(null);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      const res = await API.get("/players/my-achievements");
      setAchievements(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load achievements", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAchievements();
  }, []);

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setDocumentFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.event_name || !documentFile) {
      Swal.fire("Error", "Event Name and Certificate document are required", "error");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("event_name", form.event_name);
      formData.append("event_location", form.event_location || "");
      formData.append("event_level", form.event_level);
      formData.append("medal", form.medal);
      formData.append("event_date", form.event_date || "");
      formData.append("notes", form.notes || "");
      formData.append("document", documentFile);

      const res = await API.post("/players/my-achievements", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.success) {
        Swal.fire("Success!", "Past achievement submitted for approval.", "success");
        setShowAddForm(false);
        setForm({
          event_name: "",
          event_location: "",
          event_level: "",
          medal: "None",
          event_date: "",
          notes: "",
        });
        setDocumentFile(null);
        loadAchievements();
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to submit achievement", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 text-slate-800">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">My Past Achievements</h1>
          <p className="text-slate-500 text-sm mt-1">Submit certificates and details of your past records for validation.</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-md font-bold text-sm flex items-center gap-2 transition-all"
        >
          <FaPlus /> {showAddForm ? "Hide Form" : "Add Achievement"}
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white p-6 rounded-2xl border shadow-sm max-w-3xl">
          <h3 className="text-lg font-bold mb-4 text-slate-800">Submit New Achievement</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Event / Achievement Name *</label>
                <input
                  type="text"
                  name="event_name"
                  required
                  placeholder="E.g., Gold Medal, State Athletics Meet"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-600"
                  value={form.event_name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Certificate Proof * (PDF, Image, Max 5MB)</label>
                <input
                  type="file"
                  required
                  accept=".pdf,image/*"
                  className="w-full border rounded-xl px-4 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                  onChange={handleFileChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Event Level</label>
                <select
                  name="event_level"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-600"
                  value={form.event_level}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Level</option>
                  <option value="District">District</option>
                  <option value="State">State</option>
                  <option value="National">National</option>
                  <option value="International">International</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Medal</label>
                <select
                  name="medal"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-600"
                  value={form.medal}
                  onChange={handleInputChange}
                >
                  <option value="None">None / Participation</option>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                  <option value="Bronze">Bronze</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Event Date</label>
                <input
                  type="date"
                  name="event_date"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-600"
                  value={form.event_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Event Location</label>
                <input
                  type="text"
                  name="event_location"
                  placeholder="E.g., Visakhapatnam, AP"
                  className="w-full border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-emerald-600"
                  value={form.event_location}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Notes / Additional Info</label>
                <textarea
                  name="notes"
                  rows="2"
                  placeholder="Any details to help with verification..."
                  className="w-full border rounded-xl p-4 text-sm focus:outline-none focus:border-emerald-600"
                  value={form.notes}
                  onChange={handleInputChange}
                ></textarea>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-50 text-sm font-semibold"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2"
                disabled={submitting}
              >
                {submitting ? "Uploading..." : "Submit Record"}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-emerald-600 text-2xl animate-pulse">Loading achievements...</div>
      ) : achievements.length === 0 ? (
        <div className="bg-white border rounded-2xl p-12 text-center text-slate-500">
          <FaMedal size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="font-semibold text-lg">No past achievements submitted</p>
          <p className="text-sm mt-1">Submit records of your past games and tournaments to showcase them on your profile.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {achievements.map((ach) => (
            <div key={ach.id} className="bg-white border rounded-2xl p-5 shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-extrabold text-lg text-slate-900 leading-tight">{ach.event_name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{ach.event_location || "Location not specified"}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider flex items-center gap-1 border ${
                    ach.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    ach.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                    "bg-amber-50 text-amber-700 border-amber-200"
                  }`}>
                    {ach.status === "approved" && <FaCheckCircle />}
                    {ach.status === "rejected" && <FaTimesCircle />}
                    {ach.status === "pending" && <FaHourglassHalf />}
                    {ach.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-xs bg-slate-50 p-3 rounded-xl">
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Level</p>
                    <p className="font-bold text-slate-700 mt-0.5">{ach.event_level}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 font-bold uppercase text-[9px] tracking-wider">Medal</p>
                    <p className="font-bold text-slate-700 mt-0.5">{ach.medal}</p>
                  </div>
                  {ach.event_date && (
                    <div className="col-span-2 flex items-center gap-1.5 text-slate-500 mt-1">
                      <FaCalendarAlt size={11} />
                      <span>{new Date(ach.event_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}
                </div>
              </div>

              {ach.document_path && (
                <div className="mt-4 pt-4 border-t flex justify-end">
                  <a
                    href={resolveAssetUrl(ach.document_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700"
                  >
                    <FaFileAlt /> View Certificate Document
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAchievements;
